import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Settings, RefreshCw, Search as SearchIcon,
    AlertTriangle, AlertCircle, Info,
    Terminal, Database, XCircle, SlidersHorizontal, X,
    Copy,
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError, showSuccess, showInfo } from "../../../../utils/toast";
import LogMonitorSettings from "../LogMonitorSettings";
import { Pagination } from "../Pagination";

interface LogMonitorProps {
    asset_id: string;
    client_id: string;
    agent_id?: string;
    organization_id: string;
}

interface SeverityLevel {
    level: string;
    count: number;
}

interface LogSource {
    source_id: string;
    count: number;
}

interface LogPlatform {
    platform: string;
    count: number;
}

interface SiemensLog {
    agent_id?: number;
    asset_id?: number;
    channel?: string;
    client_id?: number;
    created?: string;
    db_log?: boolean;
    error_code?: string | number;
    event_id?: string | number;
    level?: string;
    log_timestamp?: string;
    message?: string | any;
    organization_id?: number;
    platform?: string;
    raw_data?: any;
    source_id?: string;
    source_type?: string;
    _id?: string;
}

const LogMonitor: React.FC<LogMonitorProps> = ({
    asset_id,
    client_id,
    agent_id,
    organization_id
}) => {
    // Component Mode: Dashboard vs Settings
    const [showSettings, setShowSettings] = useState<boolean>(false);

    // Logs Data States
    const [logs, setLogs] = useState<SiemensLog[]>([]);
    const [totalLogs, setTotalLogs] = useState<number>(0);
    const [levels, setLevels] = useState<SeverityLevel[]>([]);
    const [sources, setSources] = useState<LogSource[]>([]);
    const [platforms, setPlatforms] = useState<LogPlatform[]>([]);

    // Loading States
    const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
    const [loadingStats, setLoadingStats] = useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

    // Filters and Pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(200); // 200 records default
    const [searchInput, setSearchInput] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState<string>("");

    const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
    const [selectedSource, setSelectedSource] = useState<string | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);

    // Row expansion state (maps log index to boolean)
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

    const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Fetch Stats (Levels, Sources, Platforms)
    const fetchStats = useCallback(async () => {
        setLoadingStats(true);
        const params = {
            organization_id,
            client_id,
            asset_id,
            agent_id,
            db_log: false,
        };

        try {
            const [levelsRes, sourcesRes] = await Promise.all([
                baseapi.get("/siem_logs/levels", { params }),
                baseapi.get("/siem_logs/sources", { params })
            ]);

            if (levelsRes.data?.levels) {
                setLevels(levelsRes.data.levels);
            }
            if (sourcesRes.data?.sources) {
                const fetchedSources = sourcesRes.data.sources;
                setSources(fetchedSources);
                // Filter with one source by default at first
                if (fetchedSources.length > 0) {
                    setSelectedSource(prev => prev === null ? fetchedSources[0].source_id : prev);
                }
            }
            if (sourcesRes.data?.platforms) {
                setPlatforms(sourcesRes.data.platforms);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load log metrics");
        } finally {
            setLoadingStats(false);
        }
    }, [organization_id, client_id, asset_id, agent_id]);

    // Fetch Logs
    const fetchLogs = useCallback(async () => {
        setLoadingLogs(true);
        const params: any = {
            organization_id,
            client_id,
            asset_id,
            agent_id,
            page: currentPage,
            page_size: pageSize,
            db_log: false
        };

        if (selectedLevel) params.level = selectedLevel;
        if (selectedSource) params.source_id = selectedSource;
        if (selectedPlatform) params.platform = selectedPlatform;
        if (searchQuery) params.search = searchQuery;

        try {
            const response = await baseapi.get("/siem_logs/search", { params });
            const logsData =
                response.data?.logs ||
                response.data?.results ||
                response.data?.data ||
                (Array.isArray(response.data) ? response.data : null);

            if (Array.isArray(logsData)) {
                setLogs(logsData);
                setTotalLogs(
                    typeof response.data?.total === "number"
                        ? response.data.total
                        : logsData.length
                );
            } else {
                setLogs([]);
                setTotalLogs(0);
            }
            // Clear row expansion on page/filter change
            setExpandedRows({});
        } catch (error: any) {
            showError(error.message || "Failed to fetch logs");
            setLogs([]);
            setTotalLogs(0);
        } finally {
            setLoadingLogs(false);
        }
    }, [organization_id, client_id, asset_id, agent_id, currentPage, pageSize, selectedLevel, selectedSource, selectedPlatform, searchQuery]);

    // Load Stats
    useEffect(() => {
        if (!showSettings) {
            fetchStats();
        }
    }, [fetchStats, showSettings]);

    // Load Logs
    useEffect(() => {
        if (!showSettings) {
            fetchLogs();
        }
    }, [fetchLogs, showSettings]);

    // Search input change handler with debounce
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchInput(val);

        if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);

        searchDebounceTimer.current = setTimeout(() => {
            setSearchQuery(val.trim());
            setCurrentPage(1);
        }, 500);
    };

    // Clean up timer
    useEffect(() => {
        return () => {
            if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
        };
    }, []);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await Promise.all([fetchStats(), fetchLogs()]);
        setIsRefreshing(false);
    };

    // Level click toggle
    const toggleLevelFilter = (level: string) => {
        setSelectedLevel(prev => (prev === level ? null : level));
        setCurrentPage(1);
    };

    // Source click toggle


    // Clear all filters
    const clearAllFilters = () => {
        setSelectedLevel(null);
        setSelectedSource(null);
        setSelectedPlatform(null);
        setSearchInput("");
        setSearchQuery("");
        setCurrentPage(1);
    };

    // Row expansion toggle
    const toggleRow = (idx: number) => {
        setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    // Copy terminal view to clipboard
    const handleCopyLogs = () => {
        if (logs.length === 0) {
            showInfo("No logs to copy");
            return;
        }

        const getTerminalLevelText = (level?: string) => {
            const l = (level || "").toLowerCase();
            if (l.includes("critical")) return "CRITICAL";
            if (l.includes("error")) return "ERROR";
            if (l.includes("warn")) return "WARNING";
            return "INFO";
        };

        const logsText = logs
            .map((log) => {
                const timeStr = formatTerminalTime(log.log_timestamp || log.created);
                const levelStr = getTerminalLevelText(log.level);
                const messageStr = (log.message || "").replace(/[ \t]+/g, ' ');
                return `${timeStr}: ${levelStr}: ${messageStr}`;
            })
            .join("\n");

        navigator.clipboard.writeText(logsText)
            .then(() => {
                showSuccess("Logs copied to clipboard");
            })
            .catch(() => {
                showError("Failed to copy logs");
            });
    };

    // Style helper for severity levels
    const getLevelConfig = (level: string) => {
        const l = level.toLowerCase();
        if (l.includes("critical")) {
            return {
                badge: "bg-rose-50 dark:bg-red-500/10 text-rose-700 dark:text-red-400 border-rose-200 dark:border-red-500/30",
                card: "bg-rose-50/50 dark:bg-red-500/5 hover:bg-rose-100/50 dark:hover:bg-red-500/10 border-rose-200 dark:border-red-500/20 active:ring-2 active:ring-red-500/30",
                activeCard: "border-red-500 bg-rose-100/70 dark:bg-red-500/15 ring-2 ring-red-500/40",
                icon: <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-red-400" />
            };
        }
        if (l.includes("error")) {
            return {
                badge: "bg-amber-50 dark:bg-orange-500/10 text-amber-700 dark:text-orange-400 border-amber-200 dark:border-orange-500/30 font-semibold",
                card: "bg-amber-50/50 dark:bg-orange-500/5 hover:bg-amber-100/50 dark:hover:bg-orange-500/10 border-amber-200 dark:border-orange-500/20 active:ring-2 active:ring-orange-500/30",
                activeCard: "border-orange-500 bg-amber-100/70 dark:bg-orange-500/15 ring-2 ring-orange-500/40",
                icon: <AlertCircle className="w-4 h-4 text-amber-600 dark:text-orange-400" />
            };
        }
        if (l.includes("warn")) {
            return {
                badge: "bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/30",
                card: "bg-yellow-50/50 dark:bg-yellow-500/5 hover:bg-yellow-100/50 dark:hover:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/20 active:ring-2 active:ring-yellow-500/30",
                activeCard: "border-yellow-500 bg-yellow-100/70 dark:bg-yellow-500/15 ring-2 ring-yellow-500/40",
                icon: <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            };
        }
        // Info or other
        return {
            badge: "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20",
            card: "bg-blue-50/50 dark:bg-blue-500/5 hover:bg-blue-100/50 dark:hover:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 active:ring-2 active:ring-blue-500/30",
            activeCard: "border-blue-500 bg-blue-100/70 dark:bg-blue-500/15 ring-2 ring-blue-500/40",
            icon: <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        };
    };

    // Terminal plain text bracket level styling
    const getTerminalLevel = (level?: string) => {
        const l = (level || "").toLowerCase();
        if (l.includes("critical")) return <span className="text-red-600 dark:text-red-500 font-bold">CRITICAL</span>;
        if (l.includes("error")) return <span className="text-red-600 dark:text-red-500 font-bold">ERROR</span>;
        if (l.includes("warn")) return <span className="text-amber-600 dark:text-yellow-500 font-bold">WARNING</span>;
        return <span className="text-cyan-600 dark:text-cyan-400 font-bold">INFO</span>;
    };

    // Linux-like timestamp format [DD/Month HH:MM:SS]
    const formatTerminalTime = (ts?: string) => {
        if (!ts) return "-";
        try {
            const date = new Date(ts);
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = months[date.getMonth()];
            const day = date.getDate().toString().padStart(2, '0');
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            return `${day}/${month} ${hours}:${minutes}:${seconds}`;
        } catch {
            return `${ts}`;
        }
    };

    // Calculate level count from counts API, default to 0 if not found
    const getLevelCount = (levelName: string) => {
        return levels.find(l => l.level.toLowerCase() === levelName.toLowerCase())?.count ?? 0;
    };

    // Render Settings View
    if (showSettings) {
        return (
            <LogMonitorSettings
                asset_id={asset_id}
                client_id={client_id}
                agent_id={agent_id}
                onBack={() => setShowSettings(false)}
            />
        );
    }

    const totalPages = Math.max(1, Math.ceil(totalLogs / pageSize));
    const isFiltered = selectedLevel || selectedPlatform || searchQuery;

    return (
        <div className="p-3 flex flex-col h-full text-slate-900 dark:text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-2 border-b border-slate-200 dark:border-gray-700 pb-2 shrink-0">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Terminal className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Log Monitor</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            SIEM Security Logs and System Auditing
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loadingLogs || loadingStats}
                        className="p-1.5 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-orange-500' : ''}`} />
                    </button>

                    <button
                        onClick={() => setShowSettings(true)}
                        className="p-1.5 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-lg transition-colors shadow-sm flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
                        title="Log Monitor Settings"
                    >
                        <Settings className="w-3.5 h-3.5" />
                        <span>Settings</span>
                    </button>
                </div>
            </div>

            {/* Severity Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-2 shrink-0">
                {/* Total Platform Logs Card */}
                <div
                    onClick={clearAllFilters}
                    className={`border border-slate-200 dark:border-gray-800/80 rounded-xl p-2.5 cursor-pointer transition-all duration-200 shadow-sm bg-blue-50/50 dark:bg-blue-600/5 hover:bg-blue-100/50 dark:hover:bg-blue-600/10 border-blue-200 dark:border-blue-500/20 active:ring-2 active:ring-blue-500/30 ${!selectedLevel && !selectedSource && !selectedPlatform && !searchQuery
                            ? 'border-blue-500 bg-blue-100/70 dark:bg-blue-500/15 ring-2 ring-blue-500/40'
                            : ''
                        }`}
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 dark:text-gray-400">
                            {platforms[0]?.platform ? `Total (${platforms[0].platform})` : "Total Logs"}
                        </span>
                        <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex items-baseline gap-1">
                        <span className="text-lg font-black text-slate-900 dark:text-white font-mono leading-none">
                            {(platforms[0]?.count ?? totalLogs ?? 0).toLocaleString()}
                        </span>
                        <span className="text-[8px] text-slate-500 dark:text-gray-400 uppercase font-semibold">logs</span>
                    </div>
                </div>

                {["critical", "error", "warning", "info"].map((lvl) => {
                    const count = getLevelCount(lvl);
                    const cfg = getLevelConfig(lvl);
                    const isSelected = selectedLevel === lvl;

                    return (
                        <div
                            key={lvl}
                            onClick={() => toggleLevelFilter(lvl)}
                            className={`border border-slate-200 dark:border-gray-800/80 rounded-xl p-2.5 cursor-pointer transition-all duration-200 shadow-sm ${cfg.card} ${isSelected ? cfg.activeCard : ''
                                }`}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 dark:text-gray-400">
                                    {lvl}
                                </span>
                                {cfg.icon}
                            </div>
                            <div className="flex items-baseline gap-1">
                                <span className="text-lg font-black text-slate-900 dark:text-white font-mono leading-none">
                                    {count.toLocaleString()}
                                </span>
                                <span className="text-[8px] text-slate-500 dark:text-gray-400 uppercase font-semibold">logs</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Active Filters Bar */}
            {isFiltered && (
                <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-800/60 rounded-xl px-3 py-2 mb-2 flex items-center justify-between gap-4 shrink-0 shadow-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black uppercase text-slate-500 dark:text-gray-400 flex items-center gap-1">
                            <SlidersHorizontal size={10} /> Active Filters:
                        </span>

                        {selectedLevel && (
                            <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-orange-500/10 text-amber-700 dark:text-orange-400 border border-amber-200 dark:border-orange-500/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                                Level: {selectedLevel}
                                <button onClick={() => setSelectedLevel(null)} className="hover:text-slate-900 dark:hover:text-white cursor-pointer"><X size={10} /></button>
                            </span>
                        )}

                        {selectedPlatform && (
                            <span className="inline-flex items-center gap-1 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                                Platform: {selectedPlatform}
                                <button onClick={() => setSelectedPlatform(null)} className="hover:text-slate-900 dark:hover:text-white cursor-pointer"><X size={10} /></button>
                            </span>
                        )}

                        {searchQuery && (
                            <span className="inline-flex items-center gap-1 bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 px-2 py-0.5 rounded text-[10px] font-bold">
                                Match: "{searchQuery}"
                                <button onClick={() => { setSearchInput(""); setSearchQuery(""); }} className="hover:text-slate-900 dark:hover:text-white cursor-pointer"><X size={10} /></button>
                            </span>
                        )}
                    </div>

                    <button
                        onClick={clearAllFilters}
                        className="text-[10px] font-black text-rose-600 dark:text-red-400 hover:text-rose-700 dark:hover:text-red-300 uppercase tracking-widest flex items-center gap-1 border border-transparent hover:border-rose-200 dark:hover:border-red-500/25 px-2 py-0.5 rounded transition-all shrink-0 cursor-pointer"
                    >
                        Clear All
                    </button>
                </div>
            )}

            {/* Linux Terminal styled container */}
            <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-black rounded-xl border border-slate-200 dark:border-gray-800 shadow-sm dark:shadow-2xl overflow-hidden font-mono">
                {/* Terminal Header */}
                <div className="bg-slate-100 dark:bg-[#181a1f] px-4 py-2 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between shrink-0">
                    <div className="flex items-center space-x-1.5 select-none">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
                        <span className="text-[10px] text-slate-600 dark:text-gray-400 pl-2 flex items-center gap-1.5 font-mono font-medium">
                            <Terminal size={11} className="text-emerald-600 dark:text-green-500 animate-pulse" />
                            logs@siem-monitor:~/var/log/audit.log
                        </span>
                    </div>
                    <div className="text-[9px] text-slate-500 dark:text-gray-500 font-mono select-none">
                        lines: {pageSize} | total: {totalLogs.toLocaleString()}
                    </div>
                </div>

                {/* Log Sources Tabs */}
                <div className="flex items-center border-b border-slate-200 dark:border-zinc-900 bg-slate-50/80 dark:bg-[#0e1015]/80 px-4 overflow-x-auto shrink-0 select-none scrollbar-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {loadingStats ? (
                        <span className="text-xs text-slate-400 dark:text-zinc-500 py-3 px-3 italic">Loading log sources...</span>
                    ) : (
                        <>
                            {sources.map((src) => {
                                const isSelected = selectedSource === src.source_id;
                                return (
                                    <button
                                        key={src.source_id}
                                        onClick={() => setSelectedSource(src.source_id)}
                                        className={`relative flex items-center gap-2 py-2.5 px-3 text-xs font-mono transition-all border-b-2 hover:text-emerald-600 dark:hover:text-[#2dd4bf] cursor-pointer ${isSelected
                                                ? "border-emerald-600 dark:border-[#2dd4bf] text-emerald-700 dark:text-[#2dd4bf] font-bold"
                                                : "border-transparent text-slate-500 dark:text-zinc-500"
                                            }`}
                                    >
                                        <span>{src.source_id}</span>
                                        <span className={`font-mono text-[9px] px-1.5 py-0.25 rounded font-bold transition-colors ${isSelected
                                                ? "bg-emerald-100 dark:bg-[#2dd4bf]/10 text-emerald-700 dark:text-[#2dd4bf]"
                                                : "bg-slate-200 dark:bg-zinc-900 text-slate-600 dark:text-zinc-500"
                                            }`}>
                                            {src.count.toLocaleString()}
                                        </span>
                                    </button>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Log Filter Inputs */}
                <div className="p-3 border-b border-slate-200 dark:border-gray-900 flex items-center justify-between gap-3 shrink-0 bg-slate-50/50 dark:bg-zinc-950/40">
                    <div className="relative flex-1 max-w-md">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-gray-600" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={handleSearchChange}
                            placeholder="Search logs by message details..."
                            className="w-full bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800/80 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-900 dark:text-gray-200 placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:border-emerald-500 dark:focus:border-green-500 font-mono transition-colors shadow-sm"
                        />
                    </div>
                    <div className="flex items-center gap-3 select-none">
                        <button
                            onClick={handleCopyLogs}
                            disabled={logs.length === 0 || loadingLogs}
                            className="p-1 px-2.5 bg-white dark:bg-zinc-900 hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-green-400 border border-slate-200 dark:border-zinc-800 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-[10px] font-mono font-bold cursor-pointer shadow-sm"
                            title="Copy terminal view to clipboard"
                        >
                            <Copy className="w-3 h-3" />
                            <span>Copy Logs</span>
                        </button>
                    </div>
                </div>

                {/* Scrollable logs list container */}
                <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll min-h-0 bg-white dark:bg-black p-3 space-y-1.5">
                    {loadingLogs ? (
                        <div className="py-24 text-center">
                            <div className="flex flex-col items-center justify-center space-y-3">
                                <div className="w-6 h-6 border-2 border-emerald-500/20 border-t-emerald-600 dark:border-green-500/20 dark:border-t-green-500 rounded-full animate-spin"></div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-green-500 animate-pulse">Running query...</span>
                            </div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="py-24 text-center">
                            <div className="flex flex-col items-center justify-center space-y-2 text-slate-400 dark:text-zinc-650">
                                <XCircle size={32} className="text-slate-300 dark:text-zinc-800 mb-1" />
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-zinc-500">No matching logs found in shell buffer</p>
                            </div>
                        </div>
                    ) : (
                        logs.map((log, idx) => {
                            const isExpanded = !!expandedRows[idx];

                            return (
                                <React.Fragment key={idx}>
                                    <div
                                        onClick={() => toggleRow(idx)}
                                        className="hover:bg-slate-100 dark:hover:bg-zinc-900/60 p-1 rounded transition-colors cursor-pointer text-xs leading-relaxed flex items-start"
                                    >
                                        <span className="text-amber-700 dark:text-amber-500/95 shrink-0 select-none mr-2 font-medium">
                                            {formatTerminalTime(log.log_timestamp || log.created)}:
                                        </span>
                                        <span className="shrink-0 select-none mr-2">
                                            {getTerminalLevel(log.level)}:
                                        </span>
                                        <span className="text-slate-800 dark:text-green-400/90 font-mono break-all whitespace-pre-wrap flex-1">
                                            {(log.message || "").replace(/[ \t]+/g, ' ')}
                                        </span>
                                    </div>

                                    {/* Expanded Details Row */}
                                    {isExpanded && (
                                        <div className="bg-slate-50 dark:bg-zinc-950/80 px-4 py-3 border border-slate-200 dark:border-zinc-900 rounded-lg my-1.5 font-mono">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] border-b border-slate-200 dark:border-zinc-900 pb-3 text-slate-500 dark:text-zinc-400">
                                                <div className="flex flex-col">
                                                    <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">Channel</span>
                                                    <span className="text-slate-900 dark:text-zinc-300 font-mono break-all mt-0.5 bg-slate-100 dark:bg-black/50 px-1.5 py-0.5 border border-slate-200 dark:border-zinc-900 rounded">{log.channel || "-"}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">Source Type</span>
                                                    <span className="text-slate-900 dark:text-zinc-300 mt-0.5 font-semibold uppercase">{log.source_type || "-"}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">Source ID</span>
                                                    <span className="text-slate-900 dark:text-zinc-300 font-mono break-all mt-0.5 bg-slate-100 dark:bg-black/50 px-1.5 py-0.5 border border-slate-200 dark:border-zinc-900 rounded">{log.source_id || "-"}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">Event ID</span>
                                                    <span className="text-slate-900 dark:text-zinc-300 font-mono mt-0.5">{log.event_id || "-"}</span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col mt-3">
                                                <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider mb-1">Full Log Entry</span>
                                                <pre className="bg-slate-100 dark:bg-black border border-slate-200 dark:border-zinc-900 rounded-lg p-3 text-[11px] font-mono text-slate-800 dark:text-green-400/90 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all shadow-inner">
                                                    {log.message}
                                                </pre>
                                            </div>

                                            {log.raw_data && (
                                                <div className="flex flex-col mt-3">
                                                    <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider mb-1">Raw Payload Details</span>
                                                    <pre className="bg-slate-100 dark:bg-black border border-slate-200 dark:border-zinc-900 rounded-lg p-3 text-[10px] font-mono text-slate-800 dark:text-zinc-300 overflow-x-auto shadow-inner">
                                                        {JSON.stringify(log.raw_data, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </div>

                {/* Pagination Footer */}
                <div className="shrink-0 border-t border-slate-200 dark:border-zinc-900 bg-slate-50 dark:bg-zinc-950/80">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={pageSize}
                        totalItems={totalLogs}
                        onItemsPerPageChange={(val) => {
                            setPageSize(val);
                            setCurrentPage(1);
                        }}
                        itemsPerPageOptions={[50, 100, 200, 300, 400, 500]}
                    />
                </div>
            </div>
        </div>
    );
};

export default LogMonitor;
